import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { clearRetryFlag } from "./utils/lazyWithRetry";

// Clear chunk retry flag on successful app load
clearRetryFlag();

// Global handler for Vite preload errors and dynamic import failures
window.addEventListener("vite:preloadError", (event) => {
  const hasRefreshed = sessionStorage.getItem("chunk-retry-refreshed");
  if (!hasRefreshed) {
    event.preventDefault();
    sessionStorage.setItem("chunk-retry-refreshed", "true");
    const url = new URL(window.location.href);
    url.searchParams.set("_r", Date.now().toString(36));
    window.location.replace(url.toString());
  }
});

// A stale deploy can resolve a lazy chunk to an empty module, which crashes
// React with "Cannot read properties of undefined (reading 'default')" and a
// blank screen. Recover once per session with a cache-busting reload.
window.addEventListener("error", (event) => {
  const msg = event.error?.message || event.message || "";
  if (!/reading '?default'?/.test(msg)) return;
  try {
    if (sessionStorage.getItem("stale-bundle-refreshed")) return;
    sessionStorage.setItem("stale-bundle-refreshed", "true");
  } catch {
    return;
  }
  const url = new URL(window.location.href);
  url.searchParams.set("_r", Date.now().toString(36));
  window.location.replace(url.toString());
});




// Initialize PWA (only in production, not in iframes/previews)
const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();
const isPreviewHost =
  window.location.hostname.includes("id-preview--") ||
  window.location.hostname.includes("lovableproject.com");

if (!isInIframe && !isPreviewHost) {
  import("./utils/pwaUtils")
    .then(({ pwaManager }) => {
      console.log("📱 PWA initialized");
    })
    .catch(() => {});
}

// index.html ships a static description + canonical tag as a fallback for
// the home page and for anything that runs before React mounts. Once a
// page sets its own via <SEOMeta> (react-helmet-async), Helmet injects a
// NEW tag rather than replacing the static one — it has no way to "adopt"
// a tag it didn't render (Helmet-managed tags are marked data-rh="true";
// these static ones never are) — leaving two conflicting description/
// canonical tags on every page that uses SEOMeta, with the generic
// homepage one appearing first in the DOM. Remove the static ones now,
// before any page-specific tags exist, so each page's own tags (or,
// for the few pages that don't set any, none at all) are the only ones
// left — no page should ever advertise the home page's description or
// URL as its own canonical identity.
document
  .querySelectorAll('meta[name="description"], link[rel="canonical"]')
  .forEach((el) => el.remove());

const root = createRoot(document.getElementById("root")!);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
